'use client';
/**
 * LES FAMILLES D'ACTES — qui peut défaire, supprimer, décider (lot L3).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── ⚠️ CE QUE CET ÉCRAN N'EST PAS ─────────────────────────────────────────
 *
 * Ce n'est PAS « Rôles & accès » en plus fin. Les rôles disent dans
 * quelles RUBRIQUES on entre ; cet écran dit ce qu'on peut y faire
 * d'EXCEPTIONNEL — rouvrir un dossier scellé, supprimer une ligne d'un
 * registre, approuver, reconstruire un planning. Le travail ordinaire d'une
 * rubrique (créer une NC, saisir une action) n'est pas ici : il suit la
 * rôle, et c'est ce qui rend cette liste courte.
 *
 * ── ⚠️ LE PLANCHER EST MONTRÉ, PAS CACHÉ ──────────────────────────────────
 *
 * Onze familles restent au responsable qualité quoi qu'on coche. Elles
 * s'affichent, verrouillées, avec leur motif. Les masquer aurait fait chercher
 * un réglage qui n'existe pas — et surtout : la RQ doit pouvoir MONTRER, en
 * audit, la liste de ce qui lui est réservé. C'est exactement ce que le §8.3
 * lui demande de démontrer, et ce que la liste descriptive de huit lignes ne
 * savait pas faire.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { card, I, Skeleton } from '@/components/ui';
import { fetchEcriture } from '@/lib/ecrire';

import { Bouton } from '@/shared/Bouton';
type Famille = {
  code: string; libelle: string; categorie: string; plancher: boolean;
  habilitation: string | null; role: string; motif: string; routes: number;
};
type Role = { code: string; libelle: string; famille: string };
type Etat = {
  /** Version du réglage lu — renvoyée à l'enregistrement (refus 409 si périmée). */
  version?: string;
  familles: Famille[]; roles: Role[];
  plancher: string[]; reglables: string[];
  parRole: Record<string, string[]>;
};

const TITRE: Record<string, string> = {
  defaire: 'Défaire — revenir sur une décision prise',
  supprimer: 'Supprimer',
  decider: 'Décider, signer, engager',
  masse: 'Actes de masse et d’administration',
};

export function ActesReglables() {
  const [etat, setEtat] = useState<Etat | null>(null);
  const [sel, setSel] = useState<Record<string, Set<string>>>({});
  const [role, setRole] = useState('');
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await fetch('/api/ged/ged/actes', { cache: 'no-store' });
      const d = await r.json();
      if (!r.ok) { setErreur(d.error?.message || d.error || 'Lecture refusée.'); return; }
      setErreur('');
      setEtat(d);
      const init: Record<string, Set<string>> = {};
      for (const c of d.roles as Role[]) init[c.code] = new Set(d.parRole[c.code] || []);
      setSel(init);
      setRole((x) => x || (d.roles[0]?.code ?? ''));
    } catch {
      setErreur('Le service est injoignable. Rien n’a été modifié.');
    }
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void charger(); }, [charger]);

  if (erreur && !etat) return <div style={{ ...card, padding: 14, fontSize: 13 }} role="alert">{erreur}</div>;
  if (!etat) return <Skeleton rows={8} />;

  const mien = sel[role] || new Set<string>();
  const basculer = (code: string) => {
    setSel((x) => {
      const s = new Set(x[role] || []);
      if (s.has(code)) s.delete(code); else s.add(code);
      return { ...x, [role]: s };
    });
    setMessage('');
  };

  async function enregistrer() {
    setEnvoi(true); setMessage('');
    try {
      const parRole: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(sel)) parRole[k] = [...v];
      const r = await fetchEcriture('/api/ged/ged/actes', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parRole, version: etat!.version }),
      });
      const d = await r.json();
      if (!r.ok) { setMessage(d.error?.message || d.error || 'L’enregistrement a échoué.'); return; }
      setMessage(d.message || 'Enregistré.');
      await charger();
    } catch {
      setMessage('Le service est injoignable. Rien n’a été enregistré.');
    } finally { setEnvoi(false); }
  }

  const categories = ['defaire', 'supprimer', 'decider', 'masse'];

  return (
    <div>
      <div style={{
        ...card, padding: '12px 15px', marginBottom: 16, fontSize: 12.5, lineHeight: 1.65,
        background: 'var(--cyan-50)', borderColor: 'var(--cyan-200)',
        color: 'var(--text-strong)',
      }}>
        <strong>Ce ne sont pas les rubriques, ce sont les gestes d’exception.</strong> Le travail
        ordinaire d’une rubrique — créer une non-conformité, saisir une action — suit les rôles (Paramétrage → Rôles & accès)
        et n’est pas listé ici. Ne figurent que les gestes qui reviennent sur une décision, effacent
        une ligne ou engagent une signature.
        <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
          <strong>{etat.plancher.length} familles sur {etat.familles.length}</strong> restent au
          responsable qualité quoi que vous cochiez. Elles sont affichées, verrouillées, avec leur
          motif : c’est cette liste que vous montrerez en audit.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12.5, fontWeight: 600 }}>Rôle</label>
        <select value={role} onChange={(e) => setRole(e.target.value)}
          style={{
            fontSize: 13, padding: '5px 8px', fontFamily: 'inherit',
            border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
          }}>
          {etat.roles.map((c) => (
            <option key={c.code} value={c.code}>{c.libelle}</option>
          ))}
        </select>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {mien.size} famille(s) ouverte(s) sur {etat.reglables.length} réglables
        </span>
      </div>

      <div style={{ display: 'grid', gap: 14 }}>
        {categories.map((cat) => (
          <div key={cat}>
            <div style={{
              fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
              color: 'var(--text-muted)', marginBottom: 5,
            }}>{TITRE[cat]}</div>
            <div style={{ display: 'grid', gap: 5 }}>
              {etat.familles.filter((f) => f.categorie === cat).map((f) => {
                const on = mien.has(f.code);
                return (
                  <label key={f.code} style={{
                    display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.5,
                    cursor: f.plancher ? 'default' : 'pointer', opacity: f.plancher ? 0.72 : 1,
                  }}>
                    {f.plancher ? (
                      <span style={{ marginTop: 1, color: 'var(--text-muted)' }} title="Au plancher — aucun rôle ne l’ouvre">
                        <I n="lock" s={13} />
                      </span>
                    ) : (
                      <input type="checkbox" checked={on} onChange={() => basculer(f.code)}
                        style={{ accentColor: 'var(--color-primary)', marginTop: 2 }} />
                    )}
                    <span>
                      <strong>{f.libelle}</strong>
                      <span style={{ color: 'var(--text-muted)' }}> · {f.code} · {f.routes} route(s)</span>
                      {f.plancher && (
                        <span style={{
                          marginLeft: 6, fontSize: 10.5, padding: '1px 5px', borderRadius: 'var(--radius-xs)',
                          background: 'var(--gray-100)', color: 'var(--text-muted)',
                        }}>réservé au RQ</span>
                      )}
                      {f.habilitation && (
                        <span style={{
                          marginLeft: 6, fontSize: 10.5, padding: '1px 5px', borderRadius: 'var(--radius-xs)',
                          background: 'var(--amber-100)', color: 'var(--amber-800)',
                        }} title="Exigée EN PLUS du rôle, et le RQ n’en est pas dispensé">
                          habilitation {f.habilitation}
                        </span>
                      )}
                      {f.role === 'rh' && (
                        <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--text-muted)' }}>
                          (garde RH)
                        </span>
                      )}
                      <span style={{ display: 'block', color: 'var(--text-muted)' }}>{f.motif}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
        <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => void enregistrer()}>
          {envoi ? 'Enregistrement…' : 'Enregistrer'}
        </Bouton>
        {message && (
          <span style={{ fontSize: 12.5, color: message.includes('échoué') || message.includes('injoignable') ? 'var(--red-600)' : 'var(--green-700)' }}>
            {message}
          </span>
        )}
      </div>
    </div>
  );
}
